import { Control, Controller, RegisterOptions } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "../shadcn/popover";
import { Input } from "../shadcn/input";
import { Button } from "../shadcn/button";
import { Pencil, Plus } from "lucide-react";
import {
  isValidHexColor,
  adjustShadeBrightnessOnly as adjustShade,
  pickUnifiedTextColor,
} from "@/utils/generic";
import AppPreview from "../AppPreview";
import WidgetPreview from "../WidgetPreview";

interface ColorItemProps {
  value: string;
  border?: string;
  textColor?: string;
  showText?: boolean;
  isChecked: boolean;
  isBig?: boolean;
  noRing?: boolean;
  isDisabled?: boolean;
  onChange: () => void;
}

type PreviewCommonProps = {
  logo?: string;
  icon?: string;
  customization?: Record<string, any>;
};

function renderPreviewByType(
  previewType: string,
  props: PreviewCommonProps
): React.ReactElement | null {
  switch ((previewType || "").toLowerCase()) {
    case "chat":
      return <AppPreview {...props} isSmall />;
    case "form":
      return <AppPreview {...props} isForm={true} isSmall />;
    case "widget":
      return <WidgetPreview {...props} isSmall />;
    // Add more mappings here as new preview components are introduced
    default:
      return <AppPreview {...props} isForm={false} isSmall />;
  }
}

const RadioItem = (props: ColorItemProps) => {
  return (
    <label>
      <input
        type="radio"
        value={props.value}
        checked={props.isChecked}
        disabled={props.isDisabled}
        onChange={props.onChange}
        className="sr-only"
      />
      <div
        className={cn(
          "rounded-full cursor-pointer transition-all flex items-center justify-center font-semibold",
          props.showText ? "border-2" : "border-2",
          props.isBig ? "h-8 w-8 text-sm" : "h-4 w-4 text-xs",
          props.isDisabled && !props.isChecked ? "opacity-20" : "opacity-100",
          props.noRing && !props.isBig && props.isChecked && "border-1",
          props.isChecked &&
            (props.noRing
              ? "border-primary!"
              : props.isBig
                ? "ring-2 ring-[#060C11]! ring-offset-2"
                : "ring-1 ring-[#060C11] ring-offset-1")
        )}
        style={{
          backgroundColor: props.value,
          borderColor: props.border || props.value,
          color: props.textColor || props.border || "#000000",
        }}
        tabIndex={props.isDisabled ? -1 : 0}
      >
        {props.showText && "A"}
      </div>
    </label>
  );
};

interface IOption {
  value: string;
  color: string;
  border?: string;
  textColor?: string;
}

type ColorFieldValue =
  | string
  | { value: string; textValue: string; isCustom?: boolean };

interface IColorSelectProps {
  label?: string;
  value?: ColorFieldValue;
  isBig?: boolean;
  noRing?: boolean;
  preview?: string;
  isDisabled?: boolean;
  options: IOption[];
  hasCustomColor?: boolean;
  showText?: boolean; // Show "A" in color circles for badge preview
  onChange: (value: ColorFieldValue) => void;
  name?: string;
}

const isValidHex = isValidHexColor;

const parseFieldValue = (
  v: ColorFieldValue | undefined
): { value: string; isCustom: boolean } => {
  if (!v) return { value: "", isCustom: false };
  if (typeof v === "string") return { value: v, isCustom: false };
  return { value: v.value || "", isCustom: !!v.isCustom };
};

const ColorSelect = (props: IColorSelectProps) => {
  const current = useMemo(() => parseFieldValue(props.value), [props.value]);
  const [open, setOpen] = useState(false);
  const [tempColor, setTempColor] = useState<string>(
    current.value || props.options?.[0]?.color || "#000000"
  );

  const circleSize = useMemo(
    () => (props.isBig ? "h-6 w-6" : "h-4 w-4"),
    [props.isBig]
  );
  const isCustomSelected = useMemo(() => {
    return !!current.isCustom;
  }, [current.isCustom]);

  const applyCustom = (color: string) => {
    if (!isValidHex(color)) return;
    props.onChange({ value: color, textValue: color, isCustom: true });
  };

  const handleOpenChange = (next: boolean) => {
    // If closing, apply the tempColor
    if (!next) {
      if (isValidHex(tempColor)) {
        applyCustom(tempColor);
      } else {
        props.onChange("");
        setTempColor("");
      }
    }
    setOpen(next);
  };

  return (
    <div>
      {props.label && <NoxusFormLabel label={props.label} name={props.name} />}
      <div className={cn("flex items-center", props.isBig ? "gap-4" : "gap-2")}>
        {props.options.map((color, index) => (
          <RadioItem
            key={`color-option${index}`}
            value={color.color}
            border={color.border}
            textColor={color.textColor}
            showText={props.showText}
            isChecked={
              parseFieldValue(props.value).value === color.color &&
              !parseFieldValue(props.value).isCustom
            }
            isBig={props.isBig}
            noRing={props.noRing}
            isDisabled={props.isDisabled}
            onChange={() => props.onChange(color.color)}
          />
        ))}

        {props.hasCustomColor && (
          <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  "flex items-center justify-center rounded-full",
                  circleSize,
                  "focus:outline-none",
                  "cursor-pointer"
                )}
                style={{
                  backgroundColor: isCustomSelected
                    ? isValidHex(tempColor)
                      ? tempColor
                      : "#000000"
                    : "#ffffff",
                  boxShadow:
                    isCustomSelected && props.isBig
                      ? "0 0 0 2px #ffffff, 0 0 0 4px var(--primary)"
                      : undefined,
                  border: props.isBig
                    ? isCustomSelected
                      ? undefined
                      : "1px dashed var(--foreground)"
                    : isCustomSelected
                      ? "1px solid var(--foreground)"
                      : "1px dashed var(--foreground)",
                }}
                aria-label="Add custom color"
              >
                {isCustomSelected ? (
                  <Pencil
                    className={props.isBig ? "size-4" : "size-3"}
                    style={{
                      color: pickUnifiedTextColor(
                        tempColor,
                        adjustShade(tempColor)
                      ),
                    }}
                  />
                ) : !open ? (
                  <Plus className={props.isBig ? "size-4" : "size-3"} />
                ) : null}
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" side="top">
              <div className="space-y-2">
                <div>
                  {renderPreviewByType(props.preview || "chat", {
                    customization: {
                      main_color: tempColor,
                      apply_custom_theme: true,
                    },
                  })}
                </div>
                <div className="text-sm font-medium">Custom color</div>
                <div className="flex items-center gap-2">
                  <Input
                    value={tempColor}
                    onChange={(e) => {
                      const raw = e.target.value.trim();
                      if (raw === "") {
                        setTempColor("");
                        return;
                      }
                      const normalized = `#${raw.replace(/^#+/, "")}`;
                      setTempColor(normalized);
                    }}
                    placeholder="#000000"
                    className="font-mono"
                  />
                  <input
                    type="color"
                    value={isValidHex(tempColor) ? tempColor : "#000000"}
                    onChange={(e) => setTempColor(e.target.value)}
                    className="h-8 w-10 rounded-md overflow-hidden p-0 cursor-pointer"
                  />
                </div>
                <Button
                  className="w-full mt-2"
                  onClick={() => {
                    if (isValidHex(tempColor)) applyCustom(tempColor);
                    else {
                      props.onChange("");
                      setTempColor("");
                    }
                    setOpen(false);
                  }}
                >
                  Apply color
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  );
};

interface IColorSelectFieldProps {
  control: Control<any>;
  name: string;
  options: IOption[];
  hasCustomColor?: boolean;
  defaultValue?: ColorFieldValue;
  isBig?: boolean;
  noRing?: boolean;
  isDisabled?: boolean;
  showText?: boolean; // Show "A" in color circles for badge preview
  label?: string;
  rules?: RegisterOptions;
}

export const ColorSelectField = (props: IColorSelectFieldProps) => {
  const defaultValue = useMemo(() => {
    if (!props.defaultValue) return props.options[0]?.color;
    if (typeof props.defaultValue === "string") return props.defaultValue;
    return props.defaultValue.value;
  }, [props.defaultValue, props.options]);

  return (
    <Controller
      control={props.control}
      name={props.name}
      defaultValue={defaultValue}
      rules={props.rules}
      render={({ field: { onChange, value } }) => (
        <ColorSelect
          value={value}
          label={props.label}
          isBig={props.isBig}
          noRing={props.noRing}
          isDisabled={props.isDisabled}
          options={props.options}
          hasCustomColor={props.hasCustomColor}
          showText={props.showText}
          onChange={onChange}
          name={props.name}
        />
      )}
    />
  );
};
